import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  onSnapshot,
  getFirestore,
} from "firebase/firestore";
import { app } from "../../config/firebase"; // adjust path if needed
import { useListenStatus } from "../../hooks/useListenToRTDB"; // adjust path if needed

import Modal from "react-modal"; // Keeping import if other parts use it, but not used here
import "./demoPage.css";
import {
  addSubscription,
  removeSubscription,
} from "../../utils/addSubscription";
import { getFunctions, httpsCallable } from "firebase/functions";

const db = getFirestore(app);

// Modal.setAppElement("#root"); // Not needed anymore

const DemoPage = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [processing, setProcessing] = useState(false);

  // 1. Listen for users
  useEffect(() => {
    const usersRef = collection(db, "users");
    const q = query(usersRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    });

    return () => unsubscribe();
  }, []);

  // 2. Listen for online status
  const onlineStatuses = useListenStatus();

  // 3. Filter users
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    return (
      (user.displayName?.toLowerCase() || "").includes(search) ||
      (user.authUID?.toLowerCase() || "").includes(search) ||
      (user.email?.toLowerCase() || "").includes(search) ||
      (user.uid?.toLowerCase() || "").includes(search)
    );
  });

  // 4. Handle Actions with window.confirm
  const handleAction = async (user, type) => {
    const actionName = type === "add" ? "ADD" : "REMOVE";
    const confirmMessage = `Are you sure you want to ${actionName} premium subscription for ${user.displayName || "this user"}?`;

    if (window.confirm(confirmMessage)) {
      setProcessing(true);
      try {
        if (type === "add") {
          await addSubscription(app, user.authUID);
        } else {
          await removeSubscription(app, user.authUID);
        }
        // No need to manually close a modal
      } catch (error) {
        console.error("Error managing subscription:", error);
        alert("Failed to update subscription. Check console for details.");
      } finally {
        setProcessing(false);
      }
    }
  };

  const makeAdmin = async (user) => {
    if (window.confirm(`Are you sure you want to make ${user.displayName || user.email || 'this user'} an Admin?`)) {
      setProcessing(true);
      try {
        const functions = getFunctions(app);
        const setAdminRole = httpsCallable(functions, 'setAdminRole');
        const result = await setAdminRole({ email: user.email });
        alert(result.data.message);
      } catch (error) {
        console.error("Error setting admin role:", error);
        alert("Failed to make user ad admin: " + error.message);
      } finally {
        setProcessing(false);
      }
    }
  };

  const formatLastOnline = (timestamp) => {
    if (!timestamp) return "Never";
    // Check if it's a Firestore Timestamp or RTDB timestamp (number)
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : timestamp.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
    if (isNaN(date.getTime())) return "Unknown";
    return date.toLocaleString();
  };

  return (
    <div className="demo-container" style={{
      color: "white"
    }}>
      <div className="demo-header">
        <h1>User Management (Demo)</h1>
        <input
          type="text"
          placeholder="Search by name, Auth UID, or Email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-bar"
        />
      </div>

      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Auth UID</th>
              <th>Email</th>
              <th>Status</th>
              <th>Last Online</th>
              <th>Premium?</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => {
                const isOnline = onlineStatuses[user.authUID]?.isOnline;
                const lastOnline =
                  onlineStatuses[user.authUID]?.lastSeen || user.lastOnline;

                return (
                  <tr key={user.id} style={{
                    
                  }}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {user.displayPhotoUrl && (
                          <img
                            src={user.displayPhotoUrl}
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                            alt=""
                          />
                        )}
                        {user.displayName || "No Name"}
                      </div>
                    </td>
                    <td>
                      <small style={{ fontFamily: "monospace" }}>
                        {user.authUID}
                      </small>
                    </td>
                    <td>
                      <small style={{ wordBreak: 'break-all' }}>
                        {user.email || "N/A"}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`status-indicator ${isOnline ? "status-online" : "status-offline"
                          }`}
                      ></span>
                      {isOnline ? "Online" : "Offline"}
                    </td>
                    <td>{formatLastOnline(lastOnline)}</td>
                    <td>
                      {user.isPremium ? (
                        <span style={{ color: "#10b981", fontWeight: "bold" }}>
                          Premium
                        </span>
                      ) : (
                        <span style={{ color: "#6b7280" }}>Free</span>
                      )}
                    </td>
                    <td>
                      {user.isPremium ? (
                        <button
                          className="btn-sm btn-cancel"
                          disabled={processing}
                          onClick={() => handleAction(user, "remove")}
                        >
                          Cancel Sub
                        </button>
                      ) : (
                        <button
                          className="btn-sm btn-add"
                          disabled={processing}
                          onClick={() => handleAction(user, "add")}
                        >
                          Add Sub
                        </button>
                      )}

                      <button
                        className="btn-sm"
                        style={{ marginLeft: "8px", backgroundColor: "#8b5cf6", color: "white" }}
                        disabled={processing || !user.email}
                        onClick={() => makeAdmin(user)}
                      >
                        Make Admin
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="7"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DemoPage;
